// THE TRANSLATION CATALOG, AS A GRID.
//
// One row per string, one column per language the site declares, and the site's
// own text in the first column so a translator can see what they are
// translating. Every cell is an input; nothing here opens a dialog.
//
// WHY NOT THE LIST WIDGET, which is what this tab shipped with.
//
// `translations` is an array of OBJECTS, and form-schema resolves that shape to
// `list` — a READ-ONLY table with a modal per row. Adding one string is: click
// +, fill three fields in a dialog, submit, close; changing one is: find the
// row, open it, edit, submit, close. That is the right trade for `metadataList`
// and `attributes`, which hold a handful of rows each and are edited once. It
// is the wrong one for the ONE list on this screen that grows with the site:
// a catalog is one row per translated string per language, so a site with forty
// strings in three languages is a hundred and twenty trips through a dialog.
//
// WHY A MATRIX AND NOT A FLAT EDITABLE TABLE, which was the simpler option.
//
// Because of what the flat table cannot answer. `{locale, name, value}` rows in
// a list — even an inline-editable one — are sorted by nothing a person cares
// about, and "which strings have no Spanish yet" is a question you answer by
// reading every row and keeping a tally in your head. Pivoted, it is a column
// with holes in it: the gaps ARE the untranslated strings, and the count in
// each header says how many without anybody counting.
//
// The pivot also removes two of the three ways the model refuses a catalog,
// rather than re-checking them here:
//
//   an undeclared locale     a column is a locale the site declares, so there
//                            is nowhere to type one that it does not. (A locale
//                            already IN the catalog gets a column whatever the
//                            site declares — see `columnsOf`.)
//   a duplicate (locale,name) one cell per pair, by construction. The one way
//                            back to a duplicate is two rows given the same
//                            name, which is flagged on both as it is typed.
//
// `api/code/src/model/site` still checks both. This is not a second
// implementation of those rules — it is a shape in which the ordinary way of
// working does not produce them.
//
// WHAT IT IS NOT. It is not a form-schema form, and that is deliberate: the
// three fields of an entry are not what a person edits here — a CELL is, and a
// cell is (row, column) rather than a property of a document. Rendering it by
// hand also keeps the whole seed-once/merge-over-a-snapshot hazard off this tab
// (docs/form-schema.md): this component holds the grid, writes the catalog it
// derives, and never re-seeds itself from what it wrote. There is no field
// config to rebuild per render either, so P139 has nothing to detach.
import React, { useRef, useState } from "react";
import { Button, Input, Table } from "reactstrap";

/** A row is one NAME; its cells are that name's value in each locale. */
const rowOf = (id, name, values) => ({ id, name, values });

const isString = (value) => typeof value === "string";

const trimmed = (name) => (name || "").trim();

/**
 * The columns, derived on every render rather than frozen at mount.
 *
 * The site's declared locales in the order it declares them, plus any locale
 * the catalog names that the site does NOT declare. That second half is the
 * case worth being careful about: `assertLocales` refuses an undeclared locale
 * on every update, so a site that loses a language while its catalog still
 * mentions one cannot be saved at all until those entries go. Dropping the
 * column would hide the rows that have to be cleared and make the save fail
 * with an error naming a language nothing on screen shows.
 *
 * IT WAS DECIDED ONCE AT MOUNT UNTIL THE LOCALES MOVED ONTO THIS TAB, and that
 * was defensible only while they were edited on the one beside it: ContentTabs
 * keys its body on the selection, so coming back from Details remounted the
 * grid and a language declared there became a column on arrival. The form is
 * above the grid now and nothing remounts between the two, so a frozen column
 * list would mean declaring `es` and watching the grid not acquire a column
 * until something else happened to unmount it — which on this screen is a Save
 * and a reload.
 *
 * `pinned` is what survives of the freeze, and it is why this is not simply a
 * pure function of the current props. An undeclared column is on screen to be
 * CLEARED, and clearing its last cell takes its code out of `translations` —
 * so a column derived from the catalog alone would vanish under the cursor
 * mid-repair, taking the other rows' cells for that language with it. The codes
 * that were undeclared at mount stay for the session; the ones that become
 * undeclared during it (a language removed from the form above, with entries
 * still in the catalog) are found through `translations` in the same pass.
 */
const columnsOf = (locales, translations, pinned = []) => {
  const columns = [];
  const seen = new Set();

  for (const locale of locales || []) {
    const code = (locale && locale.code) || "";

    if (code && !seen.has(code)) {
      seen.add(code);
      columns.push({ code, label: (locale && locale.label) || "", declared: true });
    }
  }

  const undeclared = [
    ...pinned,
    ...(translations || []).map((entry) => (entry && entry.locale) || ""),
  ];

  for (const code of undeclared) {
    if (code && !seen.has(code)) {
      seen.add(code);
      columns.push({ code, label: "", declared: false });
    }
  }

  return columns;
};

/** The codes a catalog names that its site does not declare. */
const undeclaredIn = (locales, translations) =>
  columnsOf(locales, translations)
    .filter((column) => !column.declared)
    .map(({ code }) => code);

/**
 * The rows, decided once at mount.
 *
 * Every string the site's own `values` hold comes first, in the order the
 * Values tab shows them, WHETHER OR NOT it has been translated — an untranslated
 * string with no row would be a gap you cannot see, which is the whole point of
 * the pivot. Leaf strings only: the catalog is overlaid on the raw values and a
 * `list` or an `object` param is not a thing a catalog entry can replace
 * (docs/pagews.md § The translation catalog).
 *
 * Then any name the catalog holds that the site's values do not, which is
 * either a name typed before the value existed or a typo. Both render; the
 * empty Site value cell is what says which.
 */
const rowsOf = (translations, source, nextId) => {
  const byName = new Map();

  const rowFor = (name) => {
    if (!byName.has(name)) {
      byName.set(name, rowOf(nextId(), name, {}));
    }

    return byName.get(name);
  };

  for (const [name, value] of Object.entries(source || {})) {
    if (isString(value)) {
      rowFor(name);
    }
  }

  for (const entry of translations || []) {
    const name = (entry && entry.name) || "";

    if (name) {
      // `value` is optional on /Site/Translation, and a missing one composes as
      // the empty string — so it reads here as an empty cell, which is what it
      // renders as.
      rowFor(name).values[entry.locale] = (entry && entry.value) || "";
    }
  }

  return [...byName.values()];
};

/**
 * The catalog a grid state means.
 *
 * AN EMPTY CELL IS NOT AN ENTRY, and that is the one place this component
 * decides something rather than displaying it. An entry whose value is empty
 * does not fall through to the site's own text — `catalogOf` replaces the key
 * with the empty string — so it renders as nothing at all, in a language a
 * translator has visibly "done". There is no way to type that state here and no
 * way to see the difference from a cell that has never been filled in, so the
 * grid does not carry it: clearing a cell removes the entry and the string goes
 * back to falling back.
 *
 * The consequence to know about: a stored entry with an empty value is dropped
 * the first time this tab writes. That is a repair — the string starts falling
 * back instead of rendering blank — but it IS a change to stored data, and it
 * is the reason the round trip is pinned in the suite rather than assumed.
 *
 * A row with no name is not an entry either. It is a row somebody is about to
 * name, and it stays on screen because this component holds the rows; only the
 * derivation drops it.
 */
const entriesOf = (rows, columns) => {
  const entries = [];

  for (const row of rows) {
    const name = trimmed(row.name);

    if (!name) {
      continue;
    }

    for (const { code } of columns) {
      const value = row.values[code];

      if (value) {
        entries.push({ locale: code, name, value });
      }
    }
  }

  return entries;
};

/** Names held by more than one row: the one duplicate the pivot cannot prevent. */
const duplicatesIn = (rows) => {
  const counted = new Map();

  for (const row of rows) {
    const name = trimmed(row.name);

    if (name) {
      counted.set(name, (counted.get(name) || 0) + 1);
    }
  }

  return new Set([...counted].filter(([, count]) => count > 1).map(([name]) => name));
};

const matches = (row, filter) => {
  if (!filter) {
    return true;
  }

  const needle = filter.toLowerCase();

  return (
    row.name.toLowerCase().includes(needle) ||
    Object.values(row.values).some((value) => (value || "").toLowerCase().includes(needle))
  );
};

export const TranslationsGrid = ({
  locales,
  defaultLocale,
  source,
  translations,
  onChange,
  onSubmit,
}) => {
  // THE ROWS are seeded once, from the value object as it was when this tab
  // mounted, and never re-seeded — the same contract every form on this screen
  // has, arrived at deliberately rather than inherited. This component owns
  // them: an unnamed row somebody is halfway through typing is not in the
  // catalog it derives, so re-seeding from the catalog would delete it.
  //
  // THE COLUMNS are not, and that is the one asymmetry here. They come from
  // `locales`, which is edited by the form directly above this on the same tab,
  // and a column list frozen at mount would ignore it — see `columnsOf`. Only
  // the undeclared ones are held, and only from mount.
  const ids = useRef(0);
  const nextId = () => {
    ids.current += 1;
    return `row-${ids.current}`;
  };

  const [pinned] = useState(() => undeclaredIn(locales, translations));
  const [rows, setRows] = useState(() => rowsOf(translations, source, nextId));
  const [filter, setFilter] = useState("");

  const columns = columnsOf(locales, translations, pinned);

  // Which row's name input to put the cursor in after the next render. A ref
  // and not `autoFocus`, which fires on mount only and would fight the browser
  // on a remount.
  const focus = useRef(null);

  // WHY THERE IS NO `useWrite` HERE, on the screen that has one.
  //
  // That hook exists because a form-schema form pushes its value up from an
  // EFFECT, one commit behind the keystroke — so two forms can write in one
  // batch with no render between them and the second merge wins over the same
  // stale object (docs/form-schema.md § Bite two). Every write below is made in
  // the change handler itself, synchronously, and there is exactly one control
  // writing at a time: the `rows` a handler closes over cannot be stale, because
  // React has rendered between any two of them. The tab still writes THROUGH
  // `write` — the other tabs are the second writer, and that is the hazard the
  // hook is for.
  const update = (next) => {
    setRows(next);
    // Every write is the WHOLE catalog, and it goes up as `translations` and
    // nothing else — see the note on TranslationsTab.
    onChange(entriesOf(next, columns));
  };

  const setName = (id, name) =>
    update(rows.map((row) => (row.id === id ? { ...row, name } : row)));

  const setCell = (id, code, value) =>
    update(
      rows.map((row) =>
        row.id === id ? { ...row, values: { ...row.values, [code]: value } } : row,
      ),
    );

  const addRow = () => {
    const row = rowOf(nextId(), "", {});

    focus.current = row.id;
    // `setRows` and not `update`: an unnamed row with no cells adds nothing to
    // the catalog, so writing here would push an identical list and mark the
    // form dirty for a row that is not yet a string.
    setRows([...rows, row]);
  };

  const removeRow = (id) => update(rows.filter((row) => row.id !== id));

  const values = source || {};
  const duplicates = duplicatesIn(rows);
  const visible = rows.filter((row) => matches(row, filter));
  const named = rows.filter((row) => trimmed(row.name)).length;

  if (columns.length === 0) {
    return (
      <p className="text-muted">
        This site declares no languages yet. Add one under <b>Locales</b> above and it
        becomes a column here.
      </p>
    );
  }

  return (
    <form
      id="site-translations"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit && onSubmit();
      }}
    >
      {rows.length > 0 && (
        <Input
          className="mb-3"
          type="search"
          value={filter}
          aria-label="Filter strings"
          placeholder="Filter strings"
          onChange={(event) => setFilter(event.target.value)}
        />
      )}
      {/* Named, because it is no longer the only table on this tab: the
          Locales list widget above renders one too, and a suite reaching for
          "the table" would find whichever came first. */}
      <Table responsive size="sm" aria-label="Translation catalog">
        <thead>
          <tr>
            <th style={{ minWidth: "12rem" }}>String</th>
            <th style={{ minWidth: "12rem" }}>
              Site value
              <div className="small text-muted font-weight-normal">the fallback</div>
            </th>
            {columns.map((column) => (
              <th key={column.code} style={{ minWidth: "12rem" }}>
                {column.code}
                {column.label ? ` — ${column.label}` : ""}
                <div className="small text-muted font-weight-normal">
                  {column.declared ? (
                    <>
                      {rows.filter((row) => trimmed(row.name) && row.values[column.code]).length}{" "}
                      of {named} translated
                      {column.code === defaultLocale ? ", default" : ""}
                    </>
                  ) : (
                    <span className="text-danger">not a declared language</span>
                  )}
                </div>
              </th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {visible.map((row) => (
            <tr key={row.id}>
              <td>
                <Input
                  type="text"
                  value={row.name}
                  aria-label="String name"
                  placeholder="valueName"
                  invalid={duplicates.has(trimmed(row.name))}
                  innerRef={(node) => {
                    if (node && focus.current === row.id) {
                      focus.current = null;
                      node.focus();
                    }
                  }}
                  onChange={(event) => setName(row.id, event.target.value)}
                />
                {duplicates.has(trimmed(row.name)) && (
                  <div className="small text-danger">
                    Another row already translates this string.
                  </div>
                )}
              </td>
              <td className="text-muted">
                {/* The name a translation is looked up by is the trimmed one,
                    so this is the cell that says whether this row names
                    anything at all — an em-dash is the first thing to look at
                    when a catalog appears to do nothing. */}
                {/* `isString` and not a truth test: only a leaf string is a
                    thing a catalog entry can replace, and a name typed to match
                    a `list` or an `object` param would otherwise be handed to
                    React as an object. */}
                {isString(values[trimmed(row.name)]) && values[trimmed(row.name)] ? (
                  values[trimmed(row.name)]
                ) : (
                  <span title="No value of this name on the Values tab — a translation of it renders nothing">
                    —
                  </span>
                )}
              </td>
              {columns.map((column) => (
                <td key={column.code}>
                  <Input
                    type="text"
                    value={row.values[column.code] || ""}
                    aria-label={
                      trimmed(row.name)
                        ? `${column.code} translation of ${trimmed(row.name)}`
                        : `${column.code} translation`
                    }
                    onChange={(event) => setCell(row.id, column.code, event.target.value)}
                  />
                </td>
              ))}
              <td>
                <Button
                  type="button"
                  color="link"
                  className="text-danger"
                  title="Remove this string and every translation of it"
                  aria-label={`Remove ${trimmed(row.name) || "the empty row"}`}
                  onClick={() => removeRow(row.id)}
                >
                  <i className="fa fa-times" />
                </Button>
              </td>
            </tr>
          ))}
          {visible.length === 0 && (
            <tr>
              <td colSpan={columns.length + 3} className="text-muted">
                {rows.length === 0
                  ? "No strings yet. The site's own Values appear here as soon as it has some."
                  : "No string matches that filter."}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <Button type="button" color="secondary" onClick={addRow}>
        <i className="fa fa-plus" /> Add string
      </Button>
    </form>
  );
};
